import { css } from '@emotion/react';
import { useLayoutEffect, useMemo, useState } from 'react';

import { getProjectStateLifecycle } from '@/Client/Layout/useProjectStateLifecycle';
import {
  filterScopeNavigatorTasks,
  type ScopeAttentionFilterContext,
  type ScopeFilterKey,
  taskNeedsAttentionFlag,
} from '@/Client/Lib/scopeAttentionFilterLib';
import {
  getColorStyle,
  getColorSwatch,
  getGroupTitle,
  getPartCostValue,
  getTaskCountLabel,
  getTaskTitle,
  isScopeNavigatorTask,
  UNASSIGNED_CONTRACTOR_ID,
} from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import {
  useAuth,
  useConfirmationModal,
  useFeatureFlags,
  useScopeAttentionFiltersOptional,
  useToast,
} from '@/Client/State';
import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';
import { DropdownIcon } from '@kaiizen/kai-shiki/react';

import {
  type ScanEditingContextType,
  useScanEditingContextOrUndefined,
} from '@kaiizen/kai-pro-api-react-state';

import { isScanPartScoped, type ScanGroupOptimized, type ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';

import { ScopeFilterDropdown } from '../ScopeFilterDropdown/ScopeFilterDropdown';
import { resolveVideoToolboxClipReadStatus } from '../VideoToolbox/videoToolboxClipReadStatusLib';
import { type AddableTask, GroupAddTask } from './GroupAddTask';
import {
  canApproveTurnBasedDraftTask,
  KaiNavTaskBulkApprovalWidget,
  tasksOfferBulkApproval,
} from './KaiNavTaskApproval';
import { KaiNavTaskGroupHeader } from './KaiNavTaskGroupHeader';
import { KaiNavTaskList } from './KaiNavTaskList';
import { KaiNavTaskRow } from './KaiNavTaskRow';
import {
  amountStyle,
  formatNavigatorRollup,
  selectableRowStyle,
  TOOLS_CONTROL_HEIGHT,
} from './navigatorStyles';
import { selectRowOnEnterOrSpace } from './rowSelection';
import { useNavigatorExpansionState } from './useNavigatorExpansionState';

import { getTaskModifiers, kaiPartialIdPresenter } from '@/Client/Presenters';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { KEBAB_GUTTER_OPEN_STYLE, KEBAB_GUTTER_TRANSITION_STYLE } from '@/Client/Widgets';
import { getTaskStatusPhase, type OverallTaskStatus } from '@kaiizen/kai-pro-api';

// Single source of truth so a copy change only needs to happen here.
export const APPROVE_SCOPE_LABEL = 'Approve';
export const APPROVE_GROUP_LABEL = 'Approve';
export const APPROVE_CONTRACTOR_LABEL = 'Approve contractor';

type SidebarMode = 'group' | 'contractor';

/** The two ways the sidebar buckets the same tasks. Plural: each lists many. */
const SIDEBAR_MODES: readonly { mode: SidebarMode; label: string }[] = [
  { mode: 'group', label: 'Groups' },
  { mode: 'contractor', label: 'Contractors' },
];

type ContractorTask = {
  group: ScanGroupOptimized;
  task: ScanTaskOptimized;
};

type ContractorBucketModel = {
  id: string;
  name: string;
  tasks: readonly ContractorTask[];
  taskCount: number;
  costValue?: number;
  photoCount: number;
  videoCount: number;
  costLabel?: string;
};

const SIDEBAR_STYLE = css`
  label: CLOSEOUT_REVIEW_SIDEBAR_STYLE;
  flex: 0 0 auto;
  width: 420px;
  min-width: 320px;
  max-width: 560px;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  background: ${COLORS.paper};
  border-right: 1px solid rgba(29, 29, 29, 0.16);
  overflow: hidden;
`;

/**
 * The Scope bar is also the nav target for the whole scope — clicking it is how
 * you get back to "all groups". It sits at the top of the same hierarchy the
 * group and task rows live in (scope → group → task), so it carries the same
 * selection treatment they do rather than inventing a second convention.
 */
const SIDEBAR_HEADER_STYLE = css`
  width: 100%;
  height: 44px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  border: 0;
  border-bottom: 1px solid ${COLORS.stroke};
  flex-shrink: 0;
  background: ${COLORS.white};
  color: ${COLORS.ink};
  font: inherit;
  text-align: left;
`;

/** Hosts that cannot represent "no group selected" get the rollup without the
    affordance — no pointer, no hover, nothing to click. */
const SIDEBAR_HEADER_STATIC_STYLE = css`
  cursor: default;

  &:hover { background: ${COLORS.white}; }
`;

const SIDEBAR_HEADER_TITLE_STYLE = css`
  flex-shrink: 0;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(13)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${COLORS.ink};
`;

/** Selecting the whole scope brings its label up to full strength. */
const SIDEBAR_HEADER_TITLE_SELECTED_STYLE = css`
  color: ${COLORS.ink};
`;

const HEADER_SUBTITLE_STYLE = css`
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  padding-left: 10px;
`;

/* The approve control and the rollup ride together at the right edge. The
   cluster carries the auto margin so a control that renders nothing costs no
   layout. Reserves the same KEBAB_GUTTER_OPEN_STYLE space a group header's
   kebab does whenever the scope's own approve control renders — see
   scopeOffersApproval below — even though the scope has no kebab of its own
   to put there. */
const SIDEBAR_HEADER_TRAILING_STYLE = css`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  margin-left: auto;
`;

const SIDEBAR_HEADER_ROLLUP_STYLE = css`
  display: inline-flex;
  align-items: baseline;
  gap: 10px;
  white-space: nowrap;
`;

/* The count is body face, not mono — it reads as data alongside the total
   rather than as another label. */
const SIDEBAR_HEADER_COUNT_STYLE = css`
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.monoCaption)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${COLORS.textTertiary};
  font-variant-numeric: tabular-nums;
`;

/* Larger and heavier than a group's total, because this is the whole scope. */
const SCOPE_TOTAL_FONT_SIZE = FONT_SIZE.displaySmall;

/* ── tools row ──
   Segmented mode control, then filters, then the sidebar's own collapse caret
   pushed to the far right. Duplicating or deleting a group lives on the group
   header's own kebab now, not a toggle up here. */
const TABLIST_STYLE = css`
  label: KAI_NAVIGATOR_TOOLS_STYLE;
  flex: 0 0 auto;
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  border-bottom: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
`;

/**
 * Two segments that read as one bordered box: each carries its own border and
 * the second drops its left edge, so the shared divider is a single line.
 *
 * Giving the segments the border (rather than the wrapper) is what lets an
 * inactive one darken its own edge on hover — the signal every control in this
 * row gives — without a `:has()` rule reaching from child up to parent.
 */
const TAB_SEGMENTED_STYLE = css`
  flex-shrink: 0;
  display: inline-flex;
  height: ${TOOLS_CONTROL_HEIGHT}px;
`;

const TAB_STYLE = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border: 1px solid ${COLORS.stroke};
  background: transparent;
  color: ${COLORS.textSecondary};
  font-family: ${FONT.body};
  font-size: ${pxToRem(11)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.02em;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease, border-color 120ms ease;
`;

/** Collapses the shared edge between adjacent segments to one line. */
const TAB_ADJACENT_STYLE = css`
  border-left: 0;
`;

/**
 * The inactive segment is the only one worth hovering — the active one is
 * already where you are. Darkening its edge matches the Filters button.
 */
const TAB_HOVERABLE_STYLE = css`
  &:hover { color: ${COLORS.ink}; border-color: ${COLORS.ink}; }
`;

const ACTIVE_TAB_STYLE = css`
  background: ${COLORS.ink};
  border-color: ${COLORS.ink};
  color: ${COLORS.white};
`;

/** Pushes the collapse caret to the sidebar's right edge. */
const TAB_SPACER_STYLE = css`
  flex: 1;
  min-width: 0;
`;

const SIDEBAR_CARET_SIZE = 44;

/**
 * Shared caret style for the sidebar show/hide toggle.
 * Used by both the collapsed (expand) and visible (collapse) states so the
 * affordance renders at one consistent size and treatment.
 */
export const SIDEBAR_CARET_BUTTON_STYLE = css`
  label: CLOSEOUT_REVIEW_SIDEBAR_CARET_BUTTON_STYLE;
  flex: 0 0 ${SIDEBAR_CARET_SIZE}px;
  width: ${SIDEBAR_CARET_SIZE}px;
  height: ${SIDEBAR_CARET_SIZE}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  font: inherit;
  line-height: 1;
  cursor: pointer;
`;

const COLLAPSE_BUTTON_STYLE = [
  SIDEBAR_CARET_BUTTON_STYLE,
  css`
    label: CLOSEOUT_REVIEW_COLLAPSE_BUTTON_STYLE;
    flex-basis: 26px;
    width: 26px;
    height: 26px;
    margin-left: auto;
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.white};
    color: ${COLORS.midtone};
    box-shadow: none;

    &:hover {
      color: ${COLORS.ink};
      background: #fff;
    }
  `,
];

const SCROLL_REGION_STYLE = css`
  label: CLOSEOUT_REVIEW_SIDEBAR_SCROLL_REGION_STYLE;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  /* KAIDEV-3592: no horizontal padding — group rows extend edge-to-edge
     across the flyout (accordion style). Vertical padding only. */
  padding: 0;
`;

/* The "All groups" / "All contractors" rows are gone — the Scope header above
   is the whole-scope nav target now, so a second control for the same thing
   would be one fact stated twice. */

const GROUP_LIST_STYLE = css`
  label: CLOSEOUT_REVIEW_SIDEBAR_GROUP_LIST_STYLE;
  flex: 0 0 auto;
  /* KAIDEV-3592: no margins/gaps around group rows — flush edge-to-edge per
     the new design language. */
  margin-top: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-bottom: 0;
`;

const GROUP_DROPDOWN_STYLE = css`
  label: CLOSEOUT_REVIEW_GROUP_DROPDOWN_STYLE;
  /* No overflow hidden — sticky group headers need direct scroll-container ancestry. */
  /* Bottom border only. A border-top here would double up against the previous
     group header's own bottom border. */
  border: 0;
  border-bottom: 1px solid ${COLORS.stroke};
  border-radius: 0;
  background: ${COLORS.white};
`;

/* Neither "selected" nor "expanded" tints this container any more. Both used to
   paint the same surfaceMuted, which made the two states indistinguishable.
   Expansion is now carried by the caret and the visible task list; selection is
   carried by the lemon wash, and only ever on the header row itself — a wash on
   the container would run down the left edge of every task and read as "every
   task in this group is selected". */

/* ── add-task row (mockup: grp-new-task) ──
   A dashed block inset from the group's edges, sitting directly on the white
   task surface — the same shape as the "+ New group" affordance, so the two
   read as one system. It owns its own margin; there is no wrapper band. */
const SCOPE_GROUP_NEW_TASK_BUTTON_STYLE = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: calc(100% - 24px);
  margin: 10px 12px;
  padding: 10px;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoLabel)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: 1.5px dashed ${COLORS.stroke};
  background: transparent;
  color: ${COLORS.midtone};
  cursor: pointer;
  transition: color 120ms ease, border-color 120ms ease, background 120ms ease;

  &:hover { color: ${COLORS.ink}; border-color: ${COLORS.midtone}; background: ${COLORS.sunken}; }

  svg { width: 12px; height: 12px; stroke: currentColor; stroke-width: 1.6; fill: none; }
`;

const NO_ATTENTION_FILTERS: ReadonlySet<ScopeFilterKey> = new Set();
const EMPTY_EVAL_CONTEXT: ScopeAttentionFilterContext = {};

/**
 * Adds or removes one id, always returning a new Set so React sees the
 * change. Used only for re-clicking the group/contractor that is already the
 * whole selection (see handleSelectGroup): open→closed or closed→open,
 * neither of which ever goes through onSelectGroup/onSelectContractor, since
 * there is nowhere further to navigate to either way. Selecting a *different*
 * group opens it through the separate auto-expand effect instead, which only
 * ever adds.
 */
const toggleId = (ids: ReadonlySet<string>, id: string): ReadonlySet<string> => {
  const next = new Set(ids);
  if (!next.delete(id)) next.add(id);
  return next;
};

/**
 * Tasks shown in the sidebar: scoped or user-added (the KaiNavigator baseline),
 * further narrowed by any active attention filters (OR logic; no-op when empty).
 */
const getVisibleTasks = (
  group: ScanGroupOptimized,
  activeFilters: ReadonlySet<ScopeFilterKey>,
  evalContext: ScopeAttentionFilterContext,
): readonly ScanTaskOptimized[] => filterScopeNavigatorTasks(group.tasks ?? [], activeFilters, evalContext);

/** Tasks hidden from the sidebar but offered via the group's quick-search "+". */
const getAddableTasks = (group: ScanGroupOptimized): readonly AddableTask[] =>
  (group.tasks ?? [])
    .filter(task => !isScopeNavigatorTask(task))
    .map(task => ({
      id: task.id,
      label: getTaskTitle(task, 'Untitled task'),
      category: task.template?.category,
    }));

const sumTaskCosts = (tasks: readonly ScanTaskOptimized[]): number | undefined => {
  const costs = tasks
    .map(task => getPartCostValue(task))
    .filter((value): value is number => value != null);
  return costs.length ? costs.reduce((total, value) => total + value, 0) : undefined;
};

const getGroupCostValue = (
  group: ScanGroupOptimized,
  visibleTasks: readonly ScanTaskOptimized[],
): number | undefined => getPartCostValue(group) ?? sumTaskCosts(visibleTasks);

export const DEFAULT_GROUP_COST = '$0' as const;

const getGroupCostLabel = (
  group: ScanGroupOptimized,
  visibleTasks: readonly ScanTaskOptimized[],
): string => formatNavigatorRollup(getGroupCostValue(group, visibleTasks)) || DEFAULT_GROUP_COST;

const getTotalCostLabel = (
  groups: readonly ScanGroupOptimized[],
  activeFilters: ReadonlySet<ScopeFilterKey>,
  evalContext: ScopeAttentionFilterContext,
): string | undefined => {
  const values = groups
    .map(group => getGroupCostValue(group, getVisibleTasks(group, activeFilters, evalContext)))
    .filter((value): value is number => value != null);
  return formatNavigatorRollup(values.reduce((sum, value) => sum + value, 0));
};

/** Line items that actually carry cost — what a delete confirmation counts. */
const getScopedLineItemCount = (group: ScanGroupOptimized): number =>
  group.tasks?.flatMap(task => task.options?.flatMap(option => option.lineItems?.filter(isScanPartScoped) ?? []) ?? [])?.length ?? 0;

/**
 * Buckets the sidebar's visible tasks by the contractor who will do them.
 *
 * Keyed by org contractor id rather than by name: the id is stable, it is what
 * the route carries, and two contractors may share a name. Assignment comes from
 * the scan-editing context, which already resolves a task's explicit assignment
 * or falls back to the scan's default-for-trade.
 *
 * Unassigned leads when it has anything in it — that is the bucket with work to
 * do — and the named contractors follow alphabetically so the list is stable
 * between renders rather than ordered by whichever group happened to come first.
 */
const buildContractorBuckets = (
  groups: readonly ScanGroupOptimized[],
  activeFilters: ReadonlySet<ScopeFilterKey>,
  evalContext: ScopeAttentionFilterContext,
  getContractorForTask?: ScanEditingContextType['getContractorForTask'],
): readonly ContractorBucketModel[] => {
  const tasksByContractorId = new Map<string, ContractorTask[]>();
  const namesById = new Map<string, string>();
  const unassigned: ContractorTask[] = [];

  groups.forEach(group => {
    getVisibleTasks(group, activeFilters, evalContext).forEach(task => {
      const contractor = getContractorForTask?.(task);
      if (!contractor?.id) {
        unassigned.push({ group, task });
        return;
      }
      const { id } = contractor;
      if (!namesById.has(id)) namesById.set(id, contractor.name?.trim() || 'Unnamed contractor');
      tasksByContractorId.set(id, [...(tasksByContractorId.get(id) ?? []), { group, task }]);
    });
  });

  const toBucket = (id: string, name: string, tasks: readonly ContractorTask[]): ContractorBucketModel => {
    let costValue = 0;
    let photoCount = 0;
    let videoCount = 0;
    for (const task of tasks) {
      const _costValue = getPartCostValue(task.task);
      if (_costValue) {
        costValue += _costValue;
      }
      const _photoCount = task.task.optimized.allPhotoIds?.length ?? 0;
      if (_photoCount) {
        photoCount += _photoCount;
      }
      const _videoCount = task.task.optimized.allVideoIds?.length ?? 0;
      if (_videoCount) {
        videoCount += _videoCount;
      }
    }
    return {
      id,
      name,
      tasks,
      taskCount: tasks.length,
      photoCount,
      videoCount,
      costValue,
      costLabel: formatNavigatorRollup(costValue),
    };
  };

  const assigned = Array.from(tasksByContractorId.entries())
    .map(([id, tasks]) => toBucket(id, namesById.get(id) ?? 'Unnamed contractor', tasks))
    .sort((a, b) => a.name.localeCompare(b.name));

  return unassigned.length
    ? [toBucket(UNASSIGNED_CONTRACTOR_ID, 'Unassigned', unassigned), ...assigned]
    : assigned;
};

export const KaiNavigator = ({
  groups,
  allGroupIds,
  selectedGroupId,
  selectedContractorId,
  selectedTaskId,
  sidebarWidth,
  scopeLocked = false,
  onCollapse,
  onSelectAll,
  onSelectGroup,
  onSelectTask,
  onSelectContractor,
  onSelectContractorTask,
  onAddTask,
  onAddCustomTask,
  canAddCustomTaskForGroup,
  onCloneGroup,
  onDeleteGroup,
  onDeleteTask,
  onDuplicateTask,
}: {
  groups: readonly ScanGroupOptimized[];
  /**
   * The *unfiltered* group ids of the scan, for hosts that pass a filtered
   * `groups` (e.g. CloseoutReviewFlow filters by contractor). Expansion
   * reconciliation prunes against this, so it must span every group that exists
   * in the scan — not just the ones visible under the current filter — or a
   * transient filter would permanently collapse saved expansion. Defaults to the
   * ids of `groups` when the host renders the whole scan.
   */
  allGroupIds?: readonly string[];
  selectedGroupId?: string;
  /** Org contractor id from the route, when the Contractor tab is the way in. */
  selectedContractorId?: string;
  selectedTaskId?: string;
  sidebarWidth?: number;
  scopeLocked?: boolean;
  onCollapse: () => void;
  /** Selecting the whole scope — the Scope header is the only way in. */
  onSelectAll?: () => void;
  onSelectGroup: (groupId: string) => void;
  onSelectTask: (taskId: string) => void;
  /** Omit to leave contractor buckets browsable but not selectable. */
  onSelectContractor?: (contractorId: string) => void;
  onSelectContractorTask?: (contractorId: string, taskId: string) => void;
  onAddTask?: (groupId: string, taskId: string) => void | Promise<void>;
  onAddCustomTask?: (groupId: string, customLabel: string | undefined) => void | Promise<void>;
  canAddCustomTaskForGroup?: (group: ScanGroupOptimized) => boolean;
  onCloneGroup?: (groupId: string) => void | Promise<void>;
  onDeleteGroup?: (groupId: string) => void | Promise<void>;
  onDeleteTask?: (taskId: string) => void | Promise<void>;
  onDuplicateTask?: (taskId: string) => void | Promise<void>;
}) => {
  /**
   * Read here rather than accepted as a prop: both values live on
   * ScanEditingContext, every host of this sidebar renders inside it, and the
   * hand-assembled `context` object these used to arrive in had already
   * silently dropped `overallTaskStatusByTaskId` — every status pill fell back
   * to "scoped". Reading at the point of use makes that omission impossible.
   *
   * `OrUndefined` rather than the slice hook because the slice hook throws
   * outside a provider, and this component is rendered bare in tests.
   */
  const scanEditing = useScanEditingContextOrUndefined();
  const scanRoot = scanEditing?.scanRoot;
  const { userId } = useAuth();
  const { featureFlags } = useFeatureFlags();
  const propertyProjectId = scanEditing?.propertyProjectId ?? scanEditing?.project?.id;
  const getContractorForTask = scanEditing?.getContractorForTask;
  const overallTaskStatusByTaskId = scanEditing?.overallTaskStatusByTaskId;
  /* The filter dropdown's Modifiers group and the row tags are whatever the org has configured
     (KAIDEV-4206), enabled or not: a disabled modifier stops being offered in the kebab but its
     tagged tasks keep showing it and stay filterable. */
  const knownTaskModifiers = scanEditing?.knownTaskModifiers ?? [];
  const taskStatusPhase = getTaskStatusPhase({
    effectiveScanType: scanEditing?.effectiveScanType,
  });
  const scanId = scanEditing?.scanId;
  /**
   * The same scan facts `useProjectStateLifecycle`/`useScanEditingContextSlice` would
   * hand back, read off `scanEditing` instead of either hook — both throw outside a
   * provider, and this component is rendered bare in tests. Passed to
   * `tasksOfferBulkApproval` so a group/scope header knows whether its approve control
   * is about to render, before `KaiNavTaskBulkApprovalWidget` itself decides.
   */
  const turnBasedProject = featureFlags.turnBasedHandoff && !!scanEditing?.project;
  const canApproveTurnBasedDraft = canApproveTurnBasedDraftTask({
    turnBasedHandoff: turnBasedProject,
    managerIds: scanEditing?.project?.managerIds,
    userId,
  });
  const approvalContext = {
    projectStateLifecycle: getProjectStateLifecycle(scanEditing ?? {}),
    approvalStatusByTaskId: scanEditing?.approvalStatusByTaskId,
    scanIsChangeOrder: scanEditing?.scanIsChangeOrder ?? false,
    changedScopedTaskIds: scanEditing?.changedScopedTaskIds ?? [],
    turnBasedHandoff: turnBasedProject,
    allowTurnBasedDraftApproval: canApproveTurnBasedDraft,
  };

  const [activeMode, setActiveMode] = useState<SidebarMode>('group');
  /**
   * The active tab follows the route, not just the tab buttons. Arriving via a
   * contractor URL shows the Contractor tab even if the reader last had Groups
   * open (and vice versa) — otherwise a route the reader is genuinely on can
   * disagree with the tab that's highlighted, which is worse than not having
   * tabs at all. Browsing the other tab without selecting anything is
   * untouched by this: `selectedGroupId`/`selectedContractorId` only change on
   * an actual selection, not on opening a tab to look around.
   */
  useLayoutEffect(() => {
    if (selectedContractorId) setActiveMode('contractor');
    else if (selectedGroupId) setActiveMode('group');
  }, [selectedContractorId, selectedGroupId]);
  const [addingGroupId, setAddingGroupId] = useState<string | undefined>();
  const confirm = useConfirmationModal();
  const { showToast } = useToast();
  // Attention filters are optional here: KaiNavigator is also rendered by
  // CloseoutReviewFlow, which mounts no ScopeAttentionFilterProvider. When
  // absent, the dropdown is hidden and no filtering is applied.
  const attentionFilters = useScopeAttentionFiltersOptional();
  const activeFilters = attentionFilters?.activeFilters ?? NO_ATTENTION_FILTERS;
  const evalContext = attentionFilters?.evalContext ?? EMPTY_EVAL_CONTEXT;
  const hasActiveAttentionSelection = activeFilters.size > 0 || evalContext.taskStatus != null;
  /* Every task the filters leave standing, in group order — what the scope
     rollup counts and what its approve control commits. */
  const allVisibleTasks = useMemo(
    () => groups.flatMap(group => getVisibleTasks(group, activeFilters, evalContext)),
    [groups, activeFilters, evalContext],
  );
  const totalTasks = allVisibleTasks.length;
  const totalCostLabel = getTotalCostLabel(groups, activeFilters, evalContext);
  const contractors = useMemo(
    () => buildContractorBuckets(groups, activeFilters, evalContext, getContractorForTask),
    [groups, activeFilters, evalContext, getContractorForTask],
  );
  const validGroupIds = useMemo(
    () => allGroupIds ?? groups.map(group => group.id),
    [allGroupIds, groups],
  );
  /* Reconciliation prunes any expanded contractor id absent from this list, so it
     has to be the *unfiltered* roster. `contractors` above is built with
     `activeFilters`, so an attention filter that hides every task of a contractor
     drops that contractor's bucket entirely — yet the contractor still exists in
     the scan and its expansion must survive the filter. Rebuild the roster with
     no filters applied so pruning only ever removes ids that are genuinely gone. */
  const validContractorIds = useMemo(
    () =>
      buildContractorBuckets(groups, NO_ATTENTION_FILTERS, evalContext, getContractorForTask).map(
        contractor => contractor.id,
      ),
    [groups, evalContext, getContractorForTask],
  );
  /**
   * Every group that is open, whether the user opened it with the caret or
   * arrived at it. Any number may be open at once, which is the whole point —
   * comparing or hunting across groups shouldn't cost you the group you were
   * already looking at.
   *
   * Opening is additive and only the caret closes things. In particular, moving
   * the selection to another group opens that group and leaves the one you came
   * from open: navigating away is not a request to collapse.
   *
   * Session cache keyed by property project so a scan fork after scope approval
   * (new `:scanId`, remounted provider) restores the same accordion without a
   * one-frame collapse flash.
   */
  const {
    expandedGroupIds,
    expandedContractorIds,
    setExpandedGroupIds,
    setExpandedContractorIds,
  } = useNavigatorExpansionState(propertyProjectId, validGroupIds, validContractorIds);
  // Color identity is keyed to a group's position in the passed array; the
  // contractor tab needs this lookup because its tasks come from many groups.
  const groupIndexById = useMemo(
    () => new Map(groups.map((group, index) => [group.id, index] as const)),
    [groups],
  );
  const groupsByTemplateId = useMemo(() => {
    const byTemplateId: Record<string, ScanGroupOptimized[]> = {};
    groups.forEach(group => {
      if (!byTemplateId[group.templateId]) byTemplateId[group.templateId] = [];
      byTemplateId[group.templateId].push(group);
    });
    return byTemplateId;
  }, [groups]);
  const cloneGroup = (group: ScanGroupOptimized) => {
    onCloneGroup?.(group.id);
  };

  /** Why a group can't be deleted right now, or undefined if it's deletable. */
  const getGroupDeleteBlockReason = (group: ScanGroupOptimized): string | undefined => {
    if ((groupsByTemplateId[group.templateId]?.length ?? 0) <= 1) {
      return "This is the only group of its kind, so it can't be deleted.";
    }
    if (getScopedLineItemCount(group) > 0) {
      return "This group has scoped line items, so it can't be deleted. Remove or move its line items first.";
    }
    return undefined;
  };

  const deleteGroup = (group: ScanGroupOptimized) => {
    const blockReason = getGroupDeleteBlockReason(group);
    if (blockReason) {
      showToast(blockReason, 'error');
      return;
    }
    confirm({
      title: 'Delete Group',
      message: 'Delete this group?',
      // The shared default is "OK", which is a poor thing to click on a destructive action.
      confirmLabel: 'Delete',
      onConfirm: () => onDeleteGroup?.(group.id),
    });
  };

  /* Task row actions. A locked scope hides them outright rather than disabling them — the row
     is a reading surface first, and a dead control on every row is worse than none. */
  const canEditTasks = !scopeLocked;

  const deleteTask = (task: ScanTaskOptimized) =>
    confirm({
      title: 'Delete Task',
      message: `Delete ${getTaskTitle(task, 'this task')}? Its options and line items go with it.`,
      // The shared default is "OK", which is a poor thing to click on a destructive action.
      confirmLabel: 'Delete',
      onConfirm: () => onDeleteTask?.(task.id),
    });

  const taskRowActions = (task: ScanTaskOptimized) => ({
    onDelete: canEditTasks && onDeleteTask ? () => deleteTask(task) : undefined,
    onDuplicate: canEditTasks && onDuplicateTask ? () => onDuplicateTask(task.id) : undefined,
  });

  /**
   * The group holding the selected task, so selection can be shown all the way
   * up the containing chain rather than on one isolated row.
   */
  const selectedTaskGroupId = useMemo(
    () =>
      selectedTaskId
        ? groups.find(group => group.tasks?.some(task => task.id === selectedTaskId))?.id
        : undefined,
    [groups, selectedTaskId],
  );

  /**
   * Which group the navigator considers current. A selected task implies its
   * group — that is what makes the wash cascade.
   */
  const currentGroupId = selectedGroupId ?? selectedTaskGroupId;

  /**
   * Arriving at a group opens it — once. Selection is a nudge, not a pin: the
   * user is free to collapse the group again straight afterwards and it stays
   * collapsed while still selected. Landing here also means clicking through
   * groups never collapses the trail behind you.
   *
   * Additive and idempotent, so it can never disagree with the route the way a
   * two-way sync could: the route decides what is *selected*, this only ever
   * adds to what is *open*. Runs before paint so a shared link arrives with its
   * group already open instead of flashing shut.
   */
  useLayoutEffect(() => {
    if (!currentGroupId) return;
    setExpandedGroupIds(previous => previous.has(currentGroupId) ? previous : new Set(previous).add(currentGroupId));
  }, [currentGroupId, setExpandedGroupIds]);

  /** Open is open — the set is the whole answer, so the caret always works. */
  const isGroupExpanded = (groupId: string) => expandedGroupIds.has(groupId);

  /**
   * The contractor bucket holding the selected task, so the Contractor tab
   * cascades selection the same way the Group tab does.
   */
  const selectedTaskContractorId = useMemo(
    () =>
      selectedTaskId
        ? contractors.find(bucket => bucket.tasks.some(item => item.task.id === selectedTaskId))?.id
        : undefined,
    [contractors, selectedTaskId],
  );

  const currentContractorId = selectedContractorId ?? selectedTaskContractorId;

  /** Same one-time nudge the group tab gets: arriving at a bucket opens it. */
  useLayoutEffect(() => {
    if (!currentContractorId) return;
    setExpandedContractorIds(previous => previous.has(currentContractorId) ? previous : new Set(previous).add(currentContractorId));
  }, [currentContractorId, setExpandedContractorIds]);

  /**
   * Mirrors handleSelectGroup's own guard: the route always carries a selected
   * task's own contractor as `selectedContractorId` too, so `selectedTaskId`
   * must also be checked — otherwise clicking a bucket header while one of
   * its tasks is open would look like re-clicking an already-selected bucket
   * and never navigate back up to the contractor's full task list.
   *
   * The caret uses this exact same function too — see handleSelectGroup.
   */
  const handleSelectContractor = (contractorId: string) => {
    // Already the whole selection — nowhere further to navigate, so this
    // toggles its expansion instead. Purely a display toggle: still the
    // current contractor either way, exactly like a group's own caret/row
    // (see handleSelectGroup).
    if (contractorId === selectedContractorId && !selectedTaskId) {
      setExpandedContractorIds(previous => toggleId(previous, contractorId));
      return;
    }
    onSelectContractor?.(contractorId);
  };

  /**
   * A task's status comes from the framework, already resolved. Hosts that mount
   * the navigator without a ScanEditingContext supply no map; those rows fall
   * back to `draft`, which is the safe read for something already on the scope.
   */
  const getOverallStatus = (taskId: string): OverallTaskStatus => overallTaskStatusByTaskId?.[taskId] ?? 'draft';

  /** The modifiers a task carries, named by the org — resolved here as the row is prop-driven. */
  const getModifierLabels = (task: ScanTaskOptimized): string[] => getTaskModifiers(task, knownTaskModifiers).map(({ displayName }) => displayName);

  const getContractorName = (task: ScanTaskOptimized): string | undefined => getContractorForTask?.(task)?.name;

  const getNeedsAttentionFlag = (task: ScanTaskOptimized): boolean => taskNeedsAttentionFlag(task, evalContext);

  /**
   * Whether this host can represent "the whole scope". CloseoutReviewFlow always
   * keeps a group selected, so it passes no handler and the header stays inert.
   */
  const canSelectScope = !!onSelectAll;

  /** The whole scope is what's selected when nothing narrower is. */
  const scopeSelected = !currentGroupId && !selectedTaskId;

  /** Re-selecting the whole scope is a no-op, same rule the group rows follow. */
  const handleSelectScope = () => {
    if (scopeSelected) return;
    onSelectAll?.();
    setAddingGroupId(undefined);
  };

  const scopeOffersApproval = tasksOfferBulkApproval(allVisibleTasks, approvalContext);

  const scopeRollup = (
    <>
      <span css={[SIDEBAR_HEADER_TITLE_STYLE, scopeSelected && SIDEBAR_HEADER_TITLE_SELECTED_STYLE]}>
        {'Scope '}
        {scanId ? <span css={HEADER_SUBTITLE_STYLE}>{kaiPartialIdPresenter(scanId)}</span> : null}
      </span>
      <div css={[SIDEBAR_HEADER_TRAILING_STYLE, KEBAB_GUTTER_TRANSITION_STYLE, scopeOffersApproval && KEBAB_GUTTER_OPEN_STYLE]}>
        <span css={SIDEBAR_HEADER_ROLLUP_STYLE}>
          {totalCostLabel ? <span css={amountStyle(SCOPE_TOTAL_FONT_SIZE, FONT_WEIGHT.bold)}>{totalCostLabel}</span> : null}
          {
            /* Approving the scope is not publishing it: this files the approvals
            that let the page header's publish action light up. */
          }
          <KaiNavTaskBulkApprovalWidget tasks={allVisibleTasks} label={APPROVE_SCOPE_LABEL} />
        </span>
      </div>
    </>
  );

  /**
   * Clicking a group's header selects it and navigates to it. Re-clicking the
   * group already selected has nowhere further to navigate to, so it
   * collapses it instead — still the current group, just closed, the same
   * state a fresh selection starts collapsed in until the auto-expand effect
   * above opens it.
   *
   * That collapse only fires when the group itself is the whole selection:
   * the route always carries a selected task's own group as `selectedGroupId`
   * too, so `selectedTaskId` must also be checked — otherwise clicking a
   * group header while one of its tasks is open would be indistinguishable
   * from re-clicking an already-selected group, and collapse a group whose
   * task is still the one on screen.
   *
   * The caret uses this exact same function — it no longer holds any
   * expand/collapse state of its own. A caret that could open or close a
   * group independent of selection (and a scroll effect built to follow that
   * independent state) is what made the sidebar feel like it was
   * auto-scrolling unpredictably — expanding or collapsing a group you
   * weren't even looking at would still jump the view back to whatever *was*
   * selected, somewhere else entirely. There is no forced scrolling here at
   * all anymore. Selecting a *different* group still only ever opens it (the
   * auto-expand effect above); only re-clicking the one group that's already
   * the whole selection toggles it shut or back open.
   */
  const handleSelectGroup = (groupId: string) => {
    if (groupId === selectedGroupId && !selectedTaskId) {
      setExpandedGroupIds(previous => toggleId(previous, groupId));
      return;
    }
    onSelectGroup(groupId);
    setAddingGroupId(undefined);
  };

  const handleToggleAddTask = (groupId: string) => {
    setAddingGroupId(previous => (previous === groupId ? undefined : groupId));
    setExpandedGroupIds(previous => new Set(previous).add(groupId));
  };

  const handleAddTask = (groupId: string, taskId: string) => {
    onAddTask?.(groupId, taskId);
    setAddingGroupId(undefined);
  };

  const handleAddCustomTask = (groupId: string, customLabel: string | undefined) => {
    onAddCustomTask?.(groupId, customLabel);
    setAddingGroupId(undefined);
  };

  const handleSelectTask = (taskId: string) => {
    onSelectTask(taskId);
  };

  const handleSelectContractorTask = (contractorId: string, taskId: string) => {
    if (onSelectContractorTask) {
      onSelectContractorTask(contractorId, taskId);
      return;
    }
    handleSelectTask(taskId);
  };

  return (
    <aside
      css={SIDEBAR_STYLE}
      style={sidebarWidth == null
        ? { width: '100%', flexBasis: '100%', minWidth: 0, maxWidth: 'none', borderRightWidth: 0 }
        : { width: sidebarWidth, flexBasis: sidebarWidth }}
      aria-label='Kai scope navigation'
    >
      <div
        css={TABLIST_STYLE}
        role='tablist'
        aria-label='Closeout review navigation mode'
        data-testid='closeout-sidebar-tab-header'
      >
        <div css={TAB_SEGMENTED_STYLE}>
          {SIDEBAR_MODES.map(({ mode, label }, index) => {
            const active = activeMode === mode;
            return (
              <button
                key={mode}
                type='button'
                role='tab'
                aria-selected={active}
                css={[
                  TAB_STYLE,
                  index > 0 && TAB_ADJACENT_STYLE,
                  active ? ACTIVE_TAB_STYLE : TAB_HOVERABLE_STYLE,
                ]}
                onClick={() => setActiveMode(mode)}
              >
                {label}
              </button>
            );
          })}
        </div>
        {attentionFilters
          ? (
            <ScopeFilterDropdown
              knownTaskModifiers={knownTaskModifiers}
              groups={groups}
              filters={attentionFilters}
              overallTaskStatusByTaskId={overallTaskStatusByTaskId ?? {}}
              taskStatusPhase={taskStatusPhase}
            />
          )
          : null}
        <span css={TAB_SPACER_STYLE} />
        {sidebarWidth != null
          ? (
            /* Collapse caret only in fixed-width usage (CloseoutReviewFlow owns no
             other collapse affordance). WorkspaceLayout compositions own their
             own pulltab/collapse chrome, so the caret is omitted there. */
            <button type='button' css={COLLAPSE_BUTTON_STYLE} data-closeout-caret='collapse' aria-label='Hide sidebar' onClick={onCollapse}>
              <DropdownIcon color={COLORS.midtone} size={28} style={{ transform: 'rotate(90deg)' }} aria-hidden='true' />
            </button>
          )
          : null}
      </div>
      {
        /* ── sidebar header (mockup: sb-hdr) — also the "all groups" nav target,
          but only where the host can represent "no group selected". Without
          onSelectAll it stays a plain rollup rather than a dead control.

          A div with role="button" rather than a <button>, like the group and
          task rows: in review it carries the scope approve control, and a
          button inside a button is invalid HTML. Keyboard selection is
          therefore explicit. ── */
      }
      <div
        css={[
          SIDEBAR_HEADER_STYLE,
          canSelectScope ? selectableRowStyle({ selected: scopeSelected }) : SIDEBAR_HEADER_STATIC_STYLE,
        ]}
        data-testid='kai-navigator-scope-header'
        data-selected={canSelectScope && scopeSelected ? 'true' : 'false'}
        role={canSelectScope ? 'button' : undefined}
        title={'View the whole scope'}
        tabIndex={canSelectScope ? 0 : undefined}
        aria-pressed={canSelectScope ? scopeSelected : undefined}
        aria-label={canSelectScope
          ? `Whole scope, ${getTaskCountLabel(totalTasks)}${totalCostLabel ? `, ${totalCostLabel}` : ''}`
          : undefined}
        onClick={canSelectScope ? handleSelectScope : undefined}
        onKeyDown={canSelectScope ? selectRowOnEnterOrSpace(handleSelectScope) : undefined}
      >
        {scopeRollup}
      </div>

      <div css={SCROLL_REGION_STYLE} data-testid='closeout-sidebar-scroll-region' style={{ minHeight: 0, overflowY: 'auto' }}>
        {activeMode === 'group'
          ? (
            <>
              <div css={GROUP_LIST_STYLE} data-testid='closeout-sidebar-group-list' style={{ minHeight: 'auto' }}>
                {groups.map((group, groupIndex) => {
                  const groupSwatch = getColorSwatch(groupIndex);
                  const visibleTasks = getVisibleTasks(group, activeFilters, evalContext);
                  // With filters active, drop groups that have no matching tasks
                  // (color identity stays keyed to the original group index above).
                  if (hasActiveAttentionSelection && visibleTasks.length === 0) return null;
                  const addableTasks = getAddableTasks(group);
                  // Selection cascades: the group holding the selected task reads
                  // as selected too, so the sidebar shows the whole path to where
                  // you are instead of one orphaned row.
                  const groupSelected = group.id === currentGroupId;
                  const groupExpanded = isGroupExpanded(group.id);
                  const groupTitle = getGroupTitle(group, 'Untitled group');
                  const canAddCustomTask = !!onAddCustomTask && !!canAddCustomTaskForGroup?.(group);
                  const canAddTask = !scopeLocked && ((!!onAddTask && !!addableTasks.length) || canAddCustomTask);
                  const addTaskActive = addingGroupId === group.id;
                  return (
                    <div
                      key={group.id}
                      css={GROUP_DROPDOWN_STYLE}
                      style={getColorStyle(groupSwatch)}
                      data-testid='closeout-sidebar-group-dropdown'
                      data-closeout-color-token={groupSwatch.token}
                      data-selected={groupSelected ? 'true' : 'false'}
                      data-expanded={groupExpanded ? 'true' : 'false'}
                    >
                      <KaiNavTaskGroupHeader
                        id={group.id}
                        dropGroupId={group.id}
                        title={groupTitle}
                        photoCount={group.optimized.allPhotoIds?.length ?? 0}
                        videoCount={group.optimized.allVideoIds?.length ?? 0}
                        videoClipReadStatus={resolveVideoToolboxClipReadStatus(scanRoot, group.id)}
                        taskCount={visibleTasks.length}
                        costLabel={getGroupCostLabel(group, visibleTasks)}
                        expanded={groupExpanded}
                        selected={groupSelected}
                        onSelect={handleSelectGroup}
                        onToggleExpanded={handleSelectGroup}
                        actions={
                          // The tasks the filters leave visible are the ones this
                          // approves — the button can't commit what it can't show.
                          <KaiNavTaskBulkApprovalWidget tasks={visibleTasks} label={APPROVE_GROUP_LABEL} />
                        }
                        onDelete={onDeleteGroup ? () => deleteGroup(group) : undefined}
                        onDuplicate={onCloneGroup && !scopeLocked ? () => cloneGroup(group) : undefined}
                        approvalOffered={tasksOfferBulkApproval(visibleTasks, approvalContext)}
                      />
                      {
                        /* The picker opens in place of the row that summoned it, so
                        the group body never shows both an invitation to add and
                        the thing it opened. */
                      }
                      {groupExpanded && canAddTask
                        ? addTaskActive
                          ? (
                            <GroupAddTask
                              groupId={group.id}
                              addableTasks={addableTasks}
                              canAddCustomTask={canAddCustomTask}
                              onAdd={handleAddTask}
                              onAddCustomTask={handleAddCustomTask}
                              onClose={() => setAddingGroupId(undefined)}
                            />
                          )
                          : (
                            <button
                              type='button'
                              css={SCOPE_GROUP_NEW_TASK_BUTTON_STYLE}
                              aria-label={`Add a task to ${groupTitle}`}
                              aria-expanded={false}
                              title={`Add a task to ${groupTitle}`}
                              onClick={() => handleToggleAddTask(group.id)}
                            >
                              <svg viewBox='0 0 14 14' aria-hidden='true'>
                                <path d='M7 3v8M3 7h8' strokeLinecap='round' />
                              </svg>
                              <span>New task</span>
                            </button>
                          )
                        : null}

                      {visibleTasks.length
                        ? (
                          <KaiNavTaskList expanded={groupExpanded}>
                            {visibleTasks.map(task => (
                              <KaiNavTaskRow
                                key={task.id}
                                task={task}
                                groupId={group.id}
                                colorSwatch={groupSwatch}
                                selected={task.id === selectedTaskId}
                                overallStatus={getOverallStatus(task.id)}
                                needsAttentionFlag={getNeedsAttentionFlag(task)}
                                modifierLabels={getModifierLabels(task)}
                                onSelect={handleSelectTask}
                                {...taskRowActions(task)}
                              />
                            ))}
                          </KaiNavTaskList>
                        )
                        : null}
                    </div>
                  );
                })}
              </div>
            </>
          )
          : (
            <>
              <div css={GROUP_LIST_STYLE} data-testid='closeout-sidebar-contractor-list' style={{ minHeight: 'auto' }}>
                {contractors.map(contractor => {
                  /* Same shape as a scan group: the Contractor tab is the same
                   tasks bucketed a different way, so it gets the same header,
                   the same accordion, and the same rows. */
                  const contractorSelected = contractor.id === currentContractorId;
                  const contractorExpanded = expandedContractorIds.has(contractor.id);
                  return (
                    <div
                      key={contractor.id}
                      css={GROUP_DROPDOWN_STYLE}
                      data-testid='closeout-sidebar-contractor-dropdown'
                      data-selected={contractorSelected ? 'true' : 'false'}
                      data-expanded={contractorExpanded ? 'true' : 'false'}
                    >
                      <KaiNavTaskGroupHeader
                        id={contractor.id}
                        title={contractor.name}
                        photoCount={contractor.photoCount}
                        videoCount={contractor.videoCount}
                        taskCount={contractor.taskCount}
                        costLabel={contractor.costLabel}
                        expanded={contractorExpanded}
                        selected={contractorSelected}
                        onSelect={handleSelectContractor}
                        onToggleExpanded={handleSelectContractor}
                        actions={<KaiNavTaskBulkApprovalWidget tasks={contractor.tasks.map(item => item.task)} label={APPROVE_CONTRACTOR_LABEL} />}
                        approvalOffered={tasksOfferBulkApproval(contractor.tasks.map(item => item.task), approvalContext)}
                      />
                      {contractor.tasks.length
                        ? (
                          <KaiNavTaskList expanded={contractorExpanded}>
                            {contractor.tasks.map(item => (
                              <KaiNavTaskRow
                                key={`${item.group.id}:${item.task.id}`}
                                task={item.task}
                                groupId={item.group.id}
                                colorSwatch={getColorSwatch(groupIndexById.get(item.group.id) ?? 0)}
                                selected={item.task.id === selectedTaskId}
                                overallStatus={getOverallStatus(item.task.id)}
                                needsAttentionFlag={getNeedsAttentionFlag(item.task)}
                                onSelect={taskId => handleSelectContractorTask(contractor.id, taskId)}
                                {...taskRowActions(item.task)}
                              />
                            ))}
                          </KaiNavTaskList>
                        )
                        : null}
                    </div>
                  );
                })}
              </div>
            </>
          )}
      </div>
    </aside>
  );
};
